/**
 * CashFlowDetailTable Component
 *
 * Hierarchical cash flow tree with collapsible headers. Supports two views:
 *  - Monthly: Budget / Realized / Unrealized per account.
 *  - Weekly:  Realized per Week (1..4) + monthly Total (Budget/Realized/Unrealized).
 * A recap + saldo footer is derived from the summary payload.
 */

import React, { useMemo, useState, useCallback } from 'react';
import {
    Box,
    Button,
    Table,
    Thead,
    Tbody,
    Tfoot,
    Tr,
    Th,
    Td,
    Text,
    HStack,
    Icon,
    Skeleton,
    Center,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
    CashFlowDetailRow,
    CashFlowWeeklyRow,
    CashFlowSummary,
    CashFlowTreeNode,
    CashFlowWeeklyNode,
} from '../types';
import { buildTree, buildWeeklyTree, formatNumber } from '../utils';
import { CashFlowViewMode } from './CashFlowFilter';

const COST_COLOR = '#BD4A30';
const SECTION_BG = '#157A43';
const SUBSECTION_BG = '#E7B530';

interface CashFlowDetailTableProps {
    detail: CashFlowDetailRow[];
    weekly: CashFlowWeeklyRow[];
    summary: CashFlowSummary | null;
    viewMode: CashFlowViewMode;
    loading: boolean;
}

/** Visual style for a row based on its depth + header flag. */
const rowStyle = (depth: number, isHeader: boolean) => {
    if (!isHeader) return { bg: 'white', color: 'gray.800', fontWeight: 'normal' as const };
    if (depth === 1) return { bg: SECTION_BG, color: 'white', fontWeight: 'bold' as const };
    if (depth === 2) return { bg: SUBSECTION_BG, color: '#3A2D08', fontWeight: 'bold' as const };
    if (depth === 3) return { bg: '#F0ECE0', color: 'gray.800', fontWeight: 'semibold' as const };
    return { bg: '#F7F5EE', color: 'gray.800', fontWeight: 'medium' as const };
};

/** Numeric cell; unrealized/net values are colored by sign. */
const NumCell: React.FC<{ value: number; colorBySign?: boolean; color?: string }> = ({
    value,
    colorBySign,
    color,
}) => {
    let c = color;
    if (colorBySign) {
        if (value < 0) c = COST_COLOR;
        else if (value > 0) c = '#1E8A4E';
        else c = undefined;
    }
    return (
        <Td isNumeric fontFamily="mono" fontSize="xs" color={c} whiteSpace="nowrap">
            {formatNumber(value)}
        </Td>
    );
};

const CashFlowDetailTable: React.FC<CashFlowDetailTableProps> = ({
    detail,
    weekly,
    summary,
    viewMode,
    loading,
}) => {
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

    const monthlyTree = useMemo(() => buildTree(detail), [detail]);
    const weeklyTree = useMemo(() => buildWeeklyTree(weekly, detail), [weekly, detail]);

    const allHeaderCodes = useMemo(() => {
        const codes = new Set<string>();
        const collect = (nodes: CashFlowTreeNode[]) => {
            nodes.forEach((n) => { if (n.children.length > 0) { codes.add(n.code); collect(n.children); } });
        };
        collect(monthlyTree);
        return codes;
    }, [monthlyTree]);

    const toggle = useCallback((code: string) => {
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code);
            else next.add(code);
            return next;
        });
    }, []);

    const expandAll = useCallback(() => setCollapsed(new Set()), []);
    const collapseAll = useCallback(() => setCollapsed(new Set(allHeaderCodes)), [allHeaderCodes]);

    const isWeekly = viewMode === 'weekly';

    // Number of leading non-value columns is always 1 (the activity name).
    const NameCell: React.FC<{ node: { code: string; name: string; depth: number; is_header: boolean }; hasChildren: boolean }> = ({
        node,
        hasChildren,
    }) => {
        const style = rowStyle(node.depth, node.is_header);
        return (
            <Td
                position="sticky"
                left={0}
                zIndex={1}
                bg={style.bg}
                color={style.color}
                fontWeight={style.fontWeight}
                minW="280px"
                style={{ paddingLeft: 12 + (node.depth - 1) * 16 }}
                borderRight="1px solid"
                borderColor="gray.100"
            >
                <HStack spacing={1}>
                    {hasChildren ? (
                        <Icon
                            as={collapsed.has(node.code) ? ChevronRightIcon : ChevronDownIcon}
                            cursor="pointer"
                            onClick={() => toggle(node.code)}
                            boxSize={4}
                        />
                    ) : (
                        <Box w={4} />
                    )}
                    <Text fontSize="xs" noOfLines={2}>
                        <Text as="span" opacity={0.7} mr={2}>{node.code}</Text>
                        {node.name}
                    </Text>
                </HStack>
            </Td>
        );
    };

    // Flatten monthly tree honoring collapsed state.
    const renderMonthlyRows = (nodes: CashFlowTreeNode[]): React.ReactNode[] => {
        const rows: React.ReactNode[] = [];
        const walk = (list: CashFlowTreeNode[]) => {
            list.forEach((node) => {
                const hasChildren = node.children.length > 0;
                const style = rowStyle(node.depth, node.is_header);
                rows.push(
                    <Tr key={node.code} bg={style.bg}>
                        <NameCell node={node} hasChildren={hasChildren} />
                        <NumCell value={node.budget} color={style.color !== 'white' ? undefined : 'white'} />
                        <NumCell value={node.realized} color={style.color !== 'white' ? undefined : 'white'} />
                        <NumCell value={node.unrealized} colorBySign={!node.is_header} color={node.is_header && style.color === 'white' ? 'white' : undefined} />
                    </Tr>,
                );
                if (hasChildren && !collapsed.has(node.code)) walk(node.children);
            });
        };
        walk(nodes);
        return rows;
    };

    // Flatten weekly tree honoring collapsed state.
    const renderWeeklyRows = (nodes: CashFlowWeeklyNode[]): React.ReactNode[] => {
        const rows: React.ReactNode[] = [];
        const walk = (list: CashFlowWeeklyNode[]) => {
            list.forEach((node) => {
                const hasChildren = node.children.length > 0;
                const style = rowStyle(node.depth, node.is_header);
                const headerWhite = style.color === 'white' ? 'white' : undefined;
                rows.push(
                    <Tr key={node.code} bg={style.bg}>
                        <NameCell node={node} hasChildren={hasChildren} />
                        {node.weeks.map((w, i) => (
                            <NumCell key={i} value={w} color={headerWhite} />
                        ))}
                        <NumCell value={node.totalBudget} color={headerWhite} />
                        <NumCell value={node.totalRealized} color={headerWhite} />
                        <NumCell value={node.totalUnrealized} colorBySign={!node.is_header} color={headerWhite} />
                    </Tr>,
                );
                if (hasChildren && !collapsed.has(node.code)) walk(node.children);
            });
        };
        walk(nodes);
        return rows;
    };

    // Recap + saldo footer rows derived from summary.
    const renderRecap = (): React.ReactNode => {
        if (!summary) return null;
        const pad = isWeekly ? 4 : 0; // weekly: skip the 4 week columns, align under Total cols

        const Recap: React.FC<{ label: string; budget: number; realized: number; unrealized: number; strong?: boolean }> = ({
            label,
            budget,
            realized,
            unrealized,
            strong,
        }) => (
            <Tr bg={strong ? '#0F5C32' : '#EFF3EC'}>
                <Td
                    position="sticky"
                    left={0}
                    bg={strong ? '#0F5C32' : '#EFF3EC'}
                    color={strong ? 'white' : 'gray.800'}
                    fontWeight="bold"
                    fontSize="xs"
                    borderRight="1px solid"
                    borderColor="gray.100"
                >
                    {label}
                </Td>
                {pad > 0 && <Td colSpan={pad} bg={strong ? '#0F5C32' : '#EFF3EC'} />}
                <NumCell value={budget} color={strong ? 'white' : undefined} />
                <NumCell value={realized} color={strong ? 'white' : undefined} />
                <NumCell value={unrealized} colorBySign={!strong} color={strong ? 'white' : undefined} />
            </Tr>
        );

        return (
            <>
                <Recap
                    label="Total Revenue"
                    budget={summary.total_revenue_budget}
                    realized={summary.total_revenue_realized}
                    unrealized={summary.total_revenue_budget - summary.total_revenue_realized}
                />
                <Recap
                    label="Total Cost"
                    budget={summary.total_cost_budget}
                    realized={summary.total_cost_realized}
                    unrealized={summary.total_cost_budget - summary.total_cost_realized}
                />
                <Recap
                    label="Net Cash Before Financing"
                    budget={0}
                    realized={summary.net_realized + summary.financing_in}
                    unrealized={0}
                />
                <Recap
                    label="Net Financing"
                    budget={0}
                    realized={summary.financing_in}
                    unrealized={0}
                />
                <Recap
                    label="Net Cashflow"
                    budget={summary.net_budget}
                    realized={summary.net_realized}
                    unrealized={summary.net_budget - summary.net_realized}
                    strong
                />
                <Recap
                    label="Saldo Awal"
                    budget={summary.opening}
                    realized={summary.opening}
                    unrealized={0}
                />
                <Recap
                    label="Saldo Akhir (Realized)"
                    budget={summary.ending_balance_realized}
                    realized={summary.ending_balance_realized}
                    unrealized={0}
                    strong
                />
                <Recap
                    label="Saldo Awal (Unrealized)"
                    budget={summary.opening_balance_unrealized}
                    realized={summary.opening_balance_unrealized}
                    unrealized={0}
                />
                <Recap
                    label="Saldo Akhir (Unrealized)"
                    budget={summary.ending_balance_unrealized}
                    realized={summary.ending_balance_unrealized}
                    unrealized={0}
                    strong
                />
            </>
        );
    };

    const renderBody = (): React.ReactNode => {
        if (loading) {
            return (
                <Box p={6}>
                    <Skeleton height="400px" borderRadius="lg" />
                </Box>
            );
        }

        if (detail.length === 0) {
            return (
                <Center height="200px">
                    <Text color="gray.400" fontSize="sm">Tidak ada data detail</Text>
                </Center>
            );
        }

        return (
            <Box overflowX="auto">
                <Table size="sm" variant="simple" sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                    <Thead bg="gray.50">
                        <Tr>
                            <Th
                                position="sticky"
                                left={0}
                                zIndex={2}
                                bg="gray.50"
                                minW="280px"
                                borderRight="1px solid"
                                borderColor="gray.100"
                            >
                                Cashflow Activity
                            </Th>
                            {isWeekly ? (
                                <>
                                    <Th isNumeric>Week 1</Th>
                                    <Th isNumeric>Week 2</Th>
                                    <Th isNumeric>Week 3</Th>
                                    <Th isNumeric>Week 4</Th>
                                    <Th isNumeric>Total Budget</Th>
                                    <Th isNumeric>Total Realized</Th>
                                    <Th isNumeric>Total Unrealized</Th>
                                </>
                            ) : (
                                <>
                                    <Th isNumeric>Budget</Th>
                                    <Th isNumeric>Realized</Th>
                                    <Th isNumeric>Unrealized</Th>
                                </>
                            )}
                        </Tr>
                    </Thead>
                    <Tbody>
                        {isWeekly ? renderWeeklyRows(weeklyTree) : renderMonthlyRows(monthlyTree)}
                    </Tbody>
                    <Tfoot>{renderRecap()}</Tfoot>
                </Table>
            </Box>
        );
    };

    return (
        <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.100" overflow="hidden">
            <Box p={{ base: 4, md: 6 }} pb={2}>
                <HStack justify="space-between" align="flex-start">
                    <Box>
                        <Text fontSize="md" fontWeight="bold" color="gray.700">
                            Detail Cashflow {isWeekly ? '— Weekly (Realized)' : '— Monthly'}
                        </Text>
                        <Text fontSize="xs" color="gray.500">Budget · Realized · Unrealized</Text>
                    </Box>
                    <HStack spacing={1}>
                        <Button size="xs" variant="ghost" colorScheme="gray" onClick={expandAll}>Expand All</Button>
                        <Button size="xs" variant="ghost" colorScheme="gray" onClick={collapseAll}>Collapse All</Button>
                    </HStack>
                </HStack>
            </Box>

            {renderBody()}
        </Box>
    );
};

export default CashFlowDetailTable;
